import { usePresets } from '@/hooks/usePresets'
import type { SearchStateEntries } from '@/hooks/useSearchState'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { cn } from '@/utils/cn'
import { compareSearchParamsEquality } from '@/utils/searchParams/compareSearchParamsEquality'
import { normalizeSearchParams } from '@/utils/searchParams/normalizeSearchParams'
import { useMemo, useState } from 'react'
import { Button } from '../common/button'
import { Label } from '../common/label'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '../common/popover'
import { ScrollArea } from '../common/scrollArea'
import { ArrowIcon } from '../icons/arrow'
import { TrashIcon } from '../icons/trash'

interface Props {
  className?: string
}

export function PresetSelect({ className }: Props) {
  const [open, setOpen] = useState(false)
  const { presets, loadPreset, deletePreset, isLoading } = usePresets()
  const { searchState, updateSearchState } = useVaultsSearchState()

  const activePreset = useMemo(() => {
    if (!presets.length || isLoading) return null

    const currentParams = normalizeSearchParams(searchState)

    return presets.find((preset) => compareSearchParamsEquality(preset.params, currentParams)) || null
  }, [presets, searchState, isLoading])

  if (!presets.length || isLoading) {
    return null
  }

  const handlePresetSelect = (presetId: string) => {
    const entries = loadPreset(presetId)
    if (entries) {
      const currentParams = Array.from(searchState.keys())
      const clearEntries: SearchStateEntries = currentParams.map((key) => [key, undefined])

      updateSearchState(...clearEntries, ...entries)
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="input" size="md" className={cn('group gap-2 px-2 pl-3 text-left', className)}>
          <div className="w-full max-w-full truncate">
            <span className="truncate">{activePreset ? `Preset: ${activePreset.name}` : 'Presets'}</span>
          </div>
          <div className="group-data-[state=open]:-scale-y-100 transition-transform">
            <ArrowIcon direction="down" size={20} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="md:min-w-64" align="end">
        <ScrollArea type="auto" className={cn('flex max-h-40 flex-col overflow-y-auto')}>
          <PopoverList>
            {presets.map((preset) => {
              const isActive = activePreset?.id === preset.id

              return (
                <PopoverListItem
                  key={preset.id}
                  className={cn(
                    'relative flex h-10 w-full justify-between gap-2 pr-2 pl-4',
                    'border-t first:border-none'
                  )}
                >
                  <Button
                    id={preset.id}
                    variant="default"
                    size="default"
                    onClick={() => handlePresetSelect(preset.id)}
                    className={cn(
                      'peer relative z-[1] focus-visible:ring-0',
                      'w-full max-w-full justify-start truncate',
                      isActive && 'font-bold text-accent'
                    )}
                  >
                    <span className="max-w-full truncate">{preset.name}</span>
                  </Button>
                  <Label
                    htmlFor={preset.id}
                    className={cn(
                      'absolute inset-0 h-full w-full',
                      'rounded-xl ring-inset peer-hover:bg-surface peer-focus-visible:bg-surface peer-focus-visible:ring',
                      isActive && 'bg-surface'
                    )}
                  />
                  <Button
                    variant="default"
                    size="default"
                    title="Delete preset"
                    className="relative z-[1] flex size-7 min-w-7 items-center justify-center rounded-lg bg-destructive/10 hocus:bg-destructive/20 hocus:text-destructive text-destructive"
                    onClick={() => deletePreset(preset.id)}
                  >
                    <span className="sr-only">Delete preset</span>
                    <TrashIcon />
                  </Button>
                </PopoverListItem>
              )
            })}
          </PopoverList>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
